import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { WorkerInput } from '@jobos/shared/types/worker';

// The rate input is a text/number field, so the form works in strings and
// converts on submit: '' means "no rate", which is different from 0.
const workerFormSchema = z.object({
  name: z.string().min(1, 'Worker name is required'),
  calendarId: z.string().email('Valid calendar email/ID is required'),
  rate: z
    .string()
    .refine((v) => v === '' || !Number.isNaN(Number(v)), 'Rate must be a number')
    .refine((v) => v === '' || Number(v) >= 0, 'Rate cannot be negative'),
  active: z.boolean().optional(),
});

type WorkerFormValues = z.infer<typeof workerFormSchema>;

/** A worker's stored rate (number | null) as the form field wants it. */
function rateToField(rate: number | null | undefined): string {
  return rate === null || rate === undefined ? '' : String(rate);
}

interface WorkerFormProps {
  onSubmit: (data: WorkerInput) => void | Promise<void>;
  defaultValues?: Partial<WorkerInput>;
  isPending?: boolean;
  submitButtonText?: string;
  showCancelButton?: boolean;
  onCancel?: () => void;
}

export function WorkerForm({
  onSubmit,
  defaultValues,
  isPending = false,
  submitButtonText = 'Save Worker',
  showCancelButton = true,
  onCancel,
}: WorkerFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<WorkerFormValues>({
    resolver: zodResolver(workerFormSchema),
    defaultValues: {
      name: defaultValues?.name ?? '',
      calendarId: defaultValues?.calendarId ?? '',
      rate: rateToField(defaultValues?.rate),
      active: defaultValues?.active ?? true,
    },
  });

  // Load default values when they change (for edit mode)
  useEffect(() => {
    if (!defaultValues) return;
    if (defaultValues.name !== undefined) setValue('name', defaultValues.name);
    if (defaultValues.calendarId !== undefined) setValue('calendarId', defaultValues.calendarId);
    if (defaultValues.rate !== undefined) setValue('rate', rateToField(defaultValues.rate));
    if (defaultValues.active !== undefined) setValue('active', defaultValues.active);
  }, [defaultValues, setValue]);

  const handleFormSubmit = (data: WorkerFormValues) => {
    const trimmedRate = data.rate.trim();
    const cleanedData: WorkerInput = {
      name: data.name.trim(),
      calendarId: data.calendarId.trim(),
      rate: trimmedRate === '' ? null : Number(trimmedRate),
      active: data.active,
    };
    onSubmit(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Worker Name <span className="text-red-600">*</span>
          </label>
          <Input {...register('name')} placeholder="Enter worker name" />
          {errors.name && (
            <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
          )}
          <p className="text-sm text-gray-500 mt-1">Full name of the worker</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Calendar ID (Email) <span className="text-red-600">*</span>
          </label>
          <Input
            {...register('calendarId')}
            type="email"
            placeholder="worker@example.com"
          />
          {errors.calendarId && (
            <p className="text-sm text-red-600 mt-1">{errors.calendarId.message}</p>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Google Calendar email address for this worker. Must be accessible from your
            connected Google account.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hourly Rate
          </label>
          <Input
            {...register('rate')}
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
          />
          {errors.rate && (
            <p className="text-sm text-red-600 mt-1">{errors.rate.message}</p>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Used to price hours for this worker on generated job cards. Leave blank to
            leave the rate off the job card.
          </p>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-6">
        {showCancelButton && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Saving...' : submitButtonText}
        </Button>
      </div>
    </form>
  );
}
